// Story 7.16 (ADO-1373), part B — ANCHORED, NON-MODAL popover (NEW primitive).
// Figma "Stock Card Popover (Desktop)" 3575:10456, shown in situ at 3581:10656.
//
// ── Why a new primitive rather than reusing one ──
// Neither existing surface can do this, and both were checked before writing it:
//
//   • `design-system/Modal` is a MODAL dialog — `aria-modal`, a Tab focus trap, a
//     body-scroll lock and a centred backdrop. Anchoring it to a trigger would mean
//     removing all four, which is not an adaptation but a different component.
//   • `app/Browse/SubstitutesPanel` is the repo's existing in-card overlay and is
//     the right shape in every respect except placement: it is positioned INSIDE
//     `.card`, which is `overflow: hidden` (ProductCard.module.css:11), so it
//     cannot leave the card. The approved design has this popover overlapping
//     neighbouring grid cells (3581:10656), so a card-clipped overlay cannot
//     render it.
//
// So this portals to `<body>` and positions itself from the trigger's rect.
//
// ── a11y (NFR24-26), and why it is NOT `aria-modal` ──
// `role="dialog"` labelled by its title, Escape closes, the close button takes
// focus on open, and the CALLER restores focus to the trigger on close (the
// `SubstitutesPanel` contract, mirrored). It deliberately does NOT trap Tab or
// lock scroll: it is an inline affordance on one card in a grid, not a
// page-blocking dialog, and trapping focus in it would strand a keyboard user who
// only wanted to read a figure and move on.
//
// ── Placement ──
// Below the trigger, left-aligned, flipping above when there is not room below and
// clamped into the viewport either way. Re-measured on scroll (capturing, so
// scrolls in any ancestor count) and on resize. Below ~768px CSS takes over
// entirely and presents it as a bottom sheet, matching the mobile Stock Card
// design (1361:7370) — which is why the computed position rides CSS CUSTOM
// PROPERTIES rather than inline `top`/`left`: an inline longhand would beat the
// media query and pin the sheet mid-screen.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import styles from './Popover.module.css';

export interface PopoverProps {
  readonly open: boolean;
  /** Dismiss. The CALLER returns focus to the trigger (see the a11y note). */
  readonly onClose: () => void;
  /** Names the dialog for assistive tech, and heads the panel. */
  readonly title: string;
  /**
   * The trigger the panel is positioned against. Also the one element an outside
   * click does NOT close on: the trigger owns the open/close toggle, and closing
   * here first would make its own click immediately reopen the panel.
   */
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly children: ReactNode;
}

/** Gap between the trigger and the panel, and the minimum inset from any viewport
 *  edge. One value because the design uses one (8px). */
const VIEWPORT_MARGIN = 8;

export function Popover({ open, onClose, title, anchorRef, children }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Measured in a LAYOUT effect so the panel is placed before the browser paints;
  // until it has a position it is `visibility: hidden` (see the CSS), so it never
  // flashes at the top-left of the viewport on the way to its real home.
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor === null || panel === null) return;
      const trigger = anchor.getBoundingClientRect();
      const box = panel.getBoundingClientRect();

      const maxLeft = window.innerWidth - box.width - VIEWPORT_MARGIN;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(trigger.left, maxLeft));

      const below = trigger.bottom + VIEWPORT_MARGIN;
      const above = trigger.top - box.height - VIEWPORT_MARGIN;
      // Prefer below; flip above only when below overflows AND above actually
      // fits, so a panel taller than the viewport is not flipped into a worse
      // position. The final clamp keeps the top edge reachable either way.
      const fitsBelow = below + box.height <= window.innerHeight - VIEWPORT_MARGIN;
      const top = fitsBelow ? below : above >= VIEWPORT_MARGIN ? above : VIEWPORT_MARGIN;

      setPosition({ top, left });
    };
    reposition();
    // Capturing, so a scroll in ANY ancestor (the listing's own scroll container,
    // not just the window) re-places the panel rather than leaving it behind.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    // PR #333 review (@lozzo27): `StockCardContent` fetches asynchronously and
    // grows the panel AFTER this initial measurement. In the flip-above branch
    // `top` is computed from the panel's small initial (loading) height, so once
    // the real sold badge and week table land the panel keeps that fixed top and
    // extends DOWNWARD over the trigger — the max-height cap only adds internal
    // scroll, it does not re-place. A `ResizeObserver` on the panel itself
    // re-runs `reposition` whenever its box actually changes, not just on
    // scroll/resize/open.
    const panel = panelRef.current;
    const resizeObserver = panel && new ResizeObserver(reposition);
    if (resizeObserver && panel) resizeObserver.observe(panel);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      resizeObserver?.disconnect();
    };
  }, [open, anchorRef]);

  // Drop the stale position when the panel closes, so the next open measures fresh
  // rather than painting one frame at the previous trigger's coordinates.
  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  // Move focus into the panel on open (WAI-ARIA dialog pattern); the caller
  // restores focus to the trigger on close. `preventScroll` because the panel is
  // fixed and already in view — letting the browser scroll to it moves the page
  // under the user for no reason.
  useEffect(() => {
    if (open) closeRef.current?.focus({ preventScroll: true });
  }, [open]);

  const handleOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (panelRef.current?.contains(target) === true) return;
      // The trigger owns the toggle. Closing on ITS mousedown would be undone by
      // its own click handler reopening the panel a moment later.
      if (anchorRef.current?.contains(target) === true) return;
      onClose();
    },
    [onClose, anchorRef],
  );

  // Escape and outside-click live on the DOCUMENT rather than on the panel, so
  // both still dismiss when focus has moved outside it (a click on the page, or a
  // Tab out — neither of which this non-modal popover prevents).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', handleOutside);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', handleOutside);
    };
  }, [open, onClose, handleOutside]);

  if (!open) return null;

  // Custom properties, not inline `top`/`left`: the <768px bottom-sheet media
  // query has to be able to override the placement, and an inline longhand wins
  // against any stylesheet rule.
  const placement = {
    '--popover-top': `${position?.top ?? 0}px`,
    '--popover-left': `${position?.left ?? 0}px`,
  } as CSSProperties;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby={titleId}
      className={styles.popover}
      data-positioned={position !== null}
      style={placement}
    >
      <div className={styles.header}>
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          ref={closeRef}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className={styles.body}>{children}</div>
    </div>,
    document.body,
  );
}
